import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Organization } from '@tips-sso/shared';
import { PageHeader } from '../components/PageHeader.js';
import { DataTable, type Column } from '../components/DataTable.js';
import { Modal } from '../components/Modal.js';
import { Field, StatusBadge } from '../components/Field.js';
import { errorMessage } from '../api/client.js';
import { createReseller, fetchResellers, type CreateResellerInput } from '../api/endpoints.js';

export function ResellersPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const resellers = useQuery({ queryKey: ['resellers'], queryFn: fetchResellers });

  const columns: Column<Organization>[] = [
    { key: 'name', header: 'Reseller', render: (r) => r.displayName },
    { key: 'domain', header: 'Primary Domain', render: (r) => r.primaryDomain ?? '—' },
    { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
    {
      key: 'customers',
      header: '',
      render: (r) => (
        <button
          type="button"
          className="btn btn--secondary btn--sm"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/tipsconnect/admin/resellers/${r.id}/customers`);
          }}
        >
          Customers →
        </button>
      ),
    },
  ];

  return (
    <div className="page">
      <PageHeader
        title="Resellers"
        subtitle="Reseller organizations and their managed customers."
        actions={
          <button type="button" className="btn btn--primary" onClick={() => setOpen(true)}>
            New Reseller
          </button>
        }
      />

      <DataTable
        columns={columns}
        rows={resellers.data}
        rowKey={(r) => r.id}
        isLoading={resellers.isLoading}
        isError={resellers.isError}
        error={resellers.error}
        onRetry={() => void resellers.refetch()}
        onRowClick={(r) => navigate(`/tipsconnect/admin/resellers/${r.id}/customers`)}
        emptyMessage="No resellers yet."
      />

      <CreateResellerModal
        open={open}
        onClose={() => setOpen(false)}
        onCreated={() => {
          setOpen(false);
          void qc.invalidateQueries({ queryKey: ['resellers'] });
        }}
      />
    </div>
  );
}

function CreateResellerModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [form, setForm] = useState<CreateResellerInput>({ legalName: '', displayName: '' });
  const mutation = useMutation({ mutationFn: () => createReseller(form), onSuccess: onCreated });

  return (
    <Modal open={open} title="New Reseller" onClose={onClose}>
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
      >
        {mutation.isError && <div className="form-error">{errorMessage(mutation.error)}</div>}
        <Field label="Legal name">
          <input
            className="field__input"
            value={form.legalName}
            required
            onChange={(e) => setForm({ ...form, legalName: e.target.value })}
          />
        </Field>
        <Field label="Display name">
          <input
            className="field__input"
            value={form.displayName}
            required
            onChange={(e) => setForm({ ...form, displayName: e.target.value })}
          />
        </Field>
        <Field label="Primary domain" hint="Optional">
          <input
            className="field__input"
            value={form.primaryDomain ?? ''}
            onChange={(e) => setForm({ ...form, primaryDomain: e.target.value || undefined })}
          />
        </Field>
        <div className="form__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn--primary" disabled={mutation.isPending}>
            {mutation.isPending ? 'Creating…' : 'Create'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
